import React, { useContext, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import { type RestSimpleTextLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { OtherActions } from '@bbkFunnelComponents/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

import '../simple-link/loss-simple-link.scss';

type Props = {
  card: RestSimpleTextLaCard & {
    style_overlay?: {
      button_forwards?: {
        backgroundColor?: string;
      };
    };
  };
  onClick?: () => void;
  handleLossCardAction?: ({ action }: { action: RestModalForwards }) => void;
};

const LossSimpleText: React.FC<Props> = ({
  card,
  card: {
    max_redemptions_reached,
    id,
    requires: { copy, title, forwards },
  },
  handleLossCardAction,
  onClick,
}) => {
  const { isPreview } = useContext(FunnelContext);
  const theme = useTheme() as EmotionTheme;

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const btnRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, btnRef), [btnRef]);

  const backgroundColor = card?.style_overlay?.button_forwards?.backgroundColor;
  const equalButtonSize = theme.complianceEqualButtonSize;

  return (
    <LossGridItem
      classes={cs(
        'simple-link bbk-simple-link-survey-card bbk-simple-link-survey-card-horizontal bbk-loss-simple-link-grid-item bbk-loss-simple-link-grid-item-simple-link',
        makeClassNameSafe(card.name)
      )}
      onClick={isPreview ? onClick : () => undefined}
      isHorizontal={false}
    >
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-w-full tw-flex-col">
          <LossTitle>
            <span ref={titleRef}>
              <CustomerHtml value={title} />
            </span>
          </LossTitle>
          <div className="d-flex bbk-slsc-copy-container tw-min-h-[120px] tw-flex-1 tw-items-center">
            <div className="bbk-slsc-left-copy-container">
              <div className="copy">
                <CustomerHtml value={copy} />
              </div>
            </div>
          </div>
          {forwards && (
            <div
              className={cs('btn', {
                'tw-flex tw-justify-center': equalButtonSize,
              })}
              ref={btnRef}
            >
              <FunnelButton
                disabled={max_redemptions_reached}
                onClick={() =>
                  handleLossCardAction?.({ action: card.requires.forwards })
                }
                specificStyles={{ backgroundColor }}
                classes="d-flex align-items-center justify-content-center"
              >
                {forwards?.icon && (
                  <ActionButtonIcons
                    styles={{ marginRight: 4 }}
                    icon={forwards.icon}
                  />
                )}
                {forwards.text}
              </FunnelButton>
            </div>
          )}
          {card.requires.other_actions && (
            <OtherActions
              version2Template
              id={id}
              otherActions={card.requires.other_actions}
              onClick={(action) => handleLossCardAction?.({ action })}
            />
          )}
        </div>
      </div>
    </LossGridItem>
  );
};

export default LossSimpleText;
